/** Shared US phone formatting and validation */

export const PHONE_DISPLAY_PLACEHOLDER = '(000) 000-0000';

export const PHONE_VALIDATION_MESSAGE =
  'Please enter a valid 10-digit phone number (e.g., (123) 456-7890).';

export function extractPhoneDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

/** Strip a leading US country code when present */
export function normalizePhoneDigits(value) {
  let digits = extractPhoneDigits(value);
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits;
}

/**
 * Display / input format: (XXX) XXX-XXXX (no spaces around hyphen).
 * Accepts E.164 (+1...), digits, or already-formatted values.
 */
export function formatUSPhoneNumber(value) {
  const digits = normalizePhoneDigits(value).slice(0, 10);
  if (!digits) return '';
  if (digits.length < 4) return digits;
  if (digits.length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/** Alias for list/detail display — empty values stay empty. */
export function formatPhoneForDisplay(value, emptyValue = '') {
  if (value == null || String(value).trim() === '') return emptyValue;
  const formatted = formatUSPhoneNumber(value);
  return formatted || emptyValue;
}

/** Convert a complete US phone to E.164 (+1XXXXXXXXXX). Empty → ''. Incomplete → ''. */
export function toE164(value) {
  if (value == null || String(value).trim() === '') return '';
  const digits = normalizePhoneDigits(value);
  if (digits.length !== 10) return '';
  return `+1${digits}`;
}

export function isCompletePhoneNumber(value) {
  return normalizePhoneDigits(value).length === 10;
}

/**
 * Keep at most 10 digits. Allow delete and replace; block only net-new insertions when already full.
 */
export function reconcilePhoneDigits(previousValue, rawValue, selection = {}) {
  const { start = 0, end = 0 } = selection;
  const prev = extractPhoneDigits(previousValue).slice(0, 10);
  let next = extractPhoneDigits(rawValue);

  if (next.length === 11 && next.startsWith('1') && prev.length < 10) {
    next = next.slice(1);
  }

  if (next.length <= 10) {
    return next;
  }

  if (prev.length === 10) {
    if (end > start) {
      return next.slice(0, 10);
    }
    return prev;
  }

  return next.slice(0, 10);
}

/** Map cursor position after reformatting based on how many digits were before the cursor. */
export function getPhoneCursorAfterFormat(rawValue, formattedValue, rawCursorPos) {
  const digitsBeforeCursor = extractPhoneDigits(String(rawValue).slice(0, rawCursorPos)).length;

  if (digitsBeforeCursor <= 0) {
    return 0;
  }

  let digitsSeen = 0;
  for (let i = 0; i < formattedValue.length; i += 1) {
    if (/\d/.test(formattedValue[i])) {
      digitsSeen += 1;
      if (digitsSeen === digitsBeforeCursor) {
        return i + 1;
      }
    }
  }

  return formattedValue.length;
}

export function processPhoneInput(rawValue, previousValue = '', selection = {}) {
  const digits = reconcilePhoneDigits(previousValue, rawValue, selection);
  return {
    digits: digits ?? '',
    formatted: formatUSPhoneNumber(digits),
  };
}

/** Safe wrapper for form handlers — always returns string digits/formatted values. */
export function processPhoneFieldChange(rawValue, previousValue = '', selection = {}) {
  const result = processPhoneInput(rawValue, previousValue, selection);
  return {
    digits: String(result.digits ?? ''),
    formatted: String(result.formatted ?? ''),
  };
}

/** Real-time validation while the user is typing */
export function getPhoneTypingError(digits, { required = false, requiredLabel = 'Phone' } = {}) {
  if (!digits) {
    return required ? `${requiredLabel} is required` : null;
  }
  if (digits.length < 10) {
    return PHONE_VALIDATION_MESSAGE;
  }
  return null;
}

/** Submit / full-form validation */
export function getPhoneValidationError(value, { required = true, label = 'Phone' } = {}) {
  const digits = normalizePhoneDigits(value);
  if (!digits) {
    return required ? `${label} is required` : null;
  }
  if (digits.length !== 10) {
    return PHONE_VALIDATION_MESSAGE;
  }
  return null;
}
